import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowRight, FilePlus2, BookOpen, GraduationCap, Users, Lightbulb, TrendingUp, ChevronDown } from "lucide-react";
import TutorialHelp from "@/components/TutorialHelp";
import { UpcomingEventsCard } from "@/components/EventsWidgets";
import EmployeeDashboard from "@/pages/EmployeeDashboard";
import MentorDashboard from "@/pages/MentorDashboard";
import InstitutionDashboard from "@/pages/InstitutionDashboard";
import LatestAnnouncementCard from "@/components/LatestAnnouncementCard";

// Map every role → the dashboard component that fits it best. Reused for
// primary + secondary role rendering below.
const ROLE_LABEL = {
  employee: "Employee", intern: "Intern", mentor: "Mentor", tutor: "Tutor",
  partner_institution: "Institution", client: "Researcher", admin: "Admin",
  super_admin: "Super Admin",
};

function DashboardForRole({ role, user }) {
  if (role === "employee" || role === "intern") return <EmployeeDashboard />;
  if (role === "mentor" || role === "tutor") return <MentorDashboard />;
  if (role === "partner_institution") return <InstitutionDashboard />;
  return <ClientDashboard user={user} />;
}

export default function Dashboard() {
  const { user } = useAuth();
  // Every role the user actually holds — primary first, then secondaries,
  // deduped. `is_intern` flag also counts as an intern hat.
  const roles = React.useMemo(() => {
    const arr = [];
    if (user?.role) arr.push(user.role);
    for (const r of (user?.secondary_roles || [])) if (r && !arr.includes(r)) arr.push(r);
    if (user?.is_intern && !arr.includes("intern")) arr.push("intern");
    return arr;
  }, [user]);
  const storageKey = user?.user_id ? `kb.dash.activeRole.${user.user_id}` : "kb.dash.activeRole";
  const [active, setActive] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) return saved;
    } catch { /* ignore */ }
    return user?.role || "client";
  });
  useEffect(() => { try { localStorage.setItem(storageKey, active); } catch { /* ignore */ } }, [active, storageKey]);
  // Fall back safely if the persisted role is no longer available.
  useEffect(() => {
    if (roles.length > 0 && !roles.includes(active)) setActive(roles[0]);
  }, [roles, active]);
  const multi = roles.length > 1;
  return (
    <div data-testid="dashboard-root">
      {multi && (
        <div className="mb-4 flex items-center flex-wrap gap-2" data-testid="dashboard-role-switcher">
          <span className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">VIEW AS</span>
          <div className="inline-flex rounded-sm border border-slate-200 overflow-hidden">
            {roles.map((r) => (
              <button key={r} type="button" onClick={() => setActive(r)}
                className={`px-3 py-1.5 kb-mono text-[10px] uppercase tracking-wider border-r border-slate-200 last:border-r-0 transition-colors ${active === r ? "bg-kb-primary text-white" : "text-kb-ink-muted hover:text-kb-ink hover:bg-slate-50"}`}
                data-testid={`dashboard-role-tab-${r}`}
              >
                {ROLE_LABEL[r] || r}
              </button>
            ))}
          </div>
        </div>
      )}
      <DashboardForRole role={active} user={user} />
    </div>
  );
}

function ClientDashboard({ user }) {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);

  const stats = [
    { k: summary?.ideas_total ?? 0, l: "Ideas Submitted", icon: Lightbulb },
    { k: summary?.ideas_in_progress ?? 0, l: "In Progress", icon: TrendingUp },
    { k: summary?.ideas_published ?? 0, l: "Published", icon: BookOpen },
    { k: summary?.courses_enrolled ?? 0, l: "Courses Enrolled", icon: GraduationCap },
  ];

  return (
    <div data-testid="dashboard-overview">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">DASHBOARD</div>
          <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">
            Welcome, <em className="italic text-kb-primary">{user?.name?.split(" ")[0] || "Researcher"}</em>.
          </h1>
          <p className="text-sm text-kb-ink-muted mt-2 max-w-xl">
            Your research workspace at a glance. Submit an idea, track stages, find mentors, and grow your portfolio.
          </p>
        </div>
        <Link to="/dashboard/ideas/new" className="kb-btn-primary" data-testid="overview-new-idea-btn">
          <FilePlus2 className="w-4 h-4" /> Submit new idea
        </Link>
      </div>

      {/* Highlighted: Browse curated research ideas */}
      <div className="mt-6"><LatestAnnouncementCard /></div>
      <div className="mt-6 relative overflow-hidden kb-curated-card rounded-md p-5 sm:p-6" data-testid="dashboard-browse-ideas-cta">
        <div className="absolute -top-12 -right-12 w-44 h-44 bg-kb-purple/10 rounded-full blur-2xl" aria-hidden="true" />
        <div className="relative flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
          <div className="min-w-0">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-purple">CURATED FOR YOU</div>
            <h3 className="font-serif text-2xl mt-1.5 leading-tight text-kb-ink">Stuck for an idea? Browse <em className="italic text-kb-purple">curated research ideas</em> by KhojByte mentors.</h3>
            <p className="text-sm text-kb-ink-muted mt-1.5">Domain-wise, ready-to-execute. Perfect to spark your thesis or paper.</p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Link to="/dashboard/ideas/browse" className="kb-btn-primary text-sm" data-testid="dashboard-browse-ideas-btn">
              Browse ideas <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/explainers" className="kb-btn-outline text-sm" data-testid="dashboard-explainers-btn">
              Paper Explainers
            </Link>
          </div>
        </div>
      </div>

      <div className="mt-6 flex items-center gap-3">
        <TutorialHelp compact title="Help & Tutorials" />
        <span className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-light">Watch quick walkthroughs of every feature</span>
      </div>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200 rounded-sm overflow-hidden">
        {stats.map((s) => (
          <div key={s.l} className="bg-white p-6">
            <div className="flex items-start justify-between">
              <s.icon className="w-4 h-4 text-kb-primary" strokeWidth={1.5} />
              <span className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-light">METRIC</span>
            </div>
            <div className="font-mono text-3xl text-kb-primary mt-4">{s.k}</div>
            <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-1">{s.l}</div>
          </div>
        ))}
      </div>

      <div className="mt-10 grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 kb-card p-6">
          <div className="flex items-center justify-between">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">CONTINUE YOUR RESEARCH</div>
            <Link to="/dashboard/ideas" className="text-xs text-kb-primary hover:underline" data-testid="overview-view-all-ideas">View all</Link>
          </div>
          <p className="text-xs text-kb-ink-muted mt-1">The two workspaces you last touched — jump straight back in.</p>
          {(() => {
            const active = (summary?.recent_ideas || []).filter((i) => !["rejected"].includes(i.status)).slice(0, 2);
            return active.length > 0 ? (
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                {active.map((idea) => (
                  <Link key={idea.id} to={`/dashboard/ideas/${idea.id}`} className="block p-4 border border-slate-200 rounded-sm hover:border-kb-primary transition-colors group" data-testid={`overview-recent-workspace-${idea.id}`}>
                    <div className="flex items-center justify-between">
                      <span className="kb-mono text-[9px] uppercase tracking-wider text-kb-primary bg-kb-primary/5 border border-kb-primary/15 px-1.5 py-0.5 rounded-sm">{idea.status}</span>
                      <ArrowRight className="w-4 h-4 text-kb-ink-light group-hover:text-kb-primary" />
                    </div>
                    <div className="font-serif text-base text-kb-ink mt-2 line-clamp-2">{idea.title}</div>
                    <div className="kb-mono text-[10px] text-kb-ink-light mt-2">{idea.domain} · {new Date(idea.created_at).toLocaleDateString()}</div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="mt-4 text-center py-8 border border-dashed border-slate-300 rounded-sm">
                <Lightbulb className="w-6 h-6 text-kb-ink-light mx-auto" strokeWidth={1.5} />
                <p className="mt-2 text-sm text-kb-ink-muted">No active research yet.</p>
                <Link to="/dashboard/ideas/new" className="kb-btn-primary mt-3 text-sm inline-flex" data-testid="overview-empty-cta">
                  Submit your first idea <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            );
          })()}
          {summary?.recent_ideas?.length > 2 && (
            <>
              <div className="mt-6 kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">EARLIER RESEARCH</div>
              <ul className="mt-3 divide-y divide-slate-200">
                {summary.recent_ideas.slice(2).map((idea) => (
                  <li key={idea.id} className="py-3 flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <Link to={`/dashboard/ideas/${idea.id}`} className="block text-kb-ink hover:text-kb-primary font-medium truncate" data-testid={`overview-idea-${idea.id}`}>
                        {idea.title}
                      </Link>
                      <div className="kb-mono text-[10px] text-kb-ink-light mt-0.5">
                        {idea.domain} · {new Date(idea.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <span className="kb-mono text-[10px] uppercase text-kb-primary bg-kb-primary/5 border border-kb-primary/15 px-2 py-1 rounded-sm">
                      {idea.status}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <div className="lg:col-span-5 space-y-6">
          <div className="kb-card p-6">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">STAGE DISTRIBUTION</div>
            {summary && Object.keys(summary.stage_counts || {}).length > 0 ? (
              <ul className="mt-4 space-y-2.5">
                {Object.entries(summary.stage_counts).map(([k, v]) => (
                  <li key={k} className="flex items-center gap-3">
                    <span className="text-sm text-kb-ink min-w-[140px]">{k}</span>
                    <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-kb-primary" style={{ width: `${Math.min(100, v * 20)}%` }} />
                    </div>
                    <span className="kb-mono text-xs text-kb-ink-muted w-6 text-right">{v}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="mt-3 text-sm text-kb-ink-muted">Submit an idea to populate your pipeline.</div>
            )}
          </div>

          <div className="kb-card p-6">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">QUICK LINKS</div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <Link to="/dashboard/mentors" className="border border-slate-200 rounded-sm p-3 hover:border-kb-primary transition" data-testid="quick-mentors">
                <Users className="w-4 h-4 text-kb-primary" strokeWidth={1.5} />
                <div className="text-sm text-kb-ink mt-2 font-medium">Find mentors</div>
              </Link>
              <Link to="/dashboard/journals" className="border border-slate-200 rounded-sm p-3 hover:border-kb-primary transition" data-testid="quick-journals">
                <BookOpen className="w-4 h-4 text-kb-primary" strokeWidth={1.5} />
                <div className="text-sm text-kb-ink mt-2 font-medium">Journals & Confs</div>
              </Link>
              <Link to="/dashboard/courses" className="border border-slate-200 rounded-sm p-3 hover:border-kb-primary transition" data-testid="quick-courses">
                <GraduationCap className="w-4 h-4 text-kb-primary" strokeWidth={1.5} />
                <div className="text-sm text-kb-ink mt-2 font-medium">LMS courses</div>
              </Link>
              <Link to="/dashboard/portfolio" className="border border-slate-200 rounded-sm p-3 hover:border-kb-primary transition" data-testid="quick-portfolio">
                <Lightbulb className="w-4 h-4 text-kb-primary" strokeWidth={1.5} />
                <div className="text-sm text-kb-ink mt-2 font-medium">My portfolio</div>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-10">
        <UpcomingEventsCard title="Live webinars & workshops" />
      </div>

      <div className="mt-10">
        <TutorialHelp title="Recommended Tutorials" />
      </div>
    </div>
  );
}
